import React from 'react';
import './PrivacyStatementSection.css';

const PrivacyStatementSection: React.FC = () => {
  return (
    <div className="privacy-statement-section">
      <div className="privacy-container">
        {/* Header Section */}
        <div className="privacy-header">
          <h1 className="privacy-title">Data Privacy Statement and Digital Marketing Statement</h1>
          <p className="privacy-subtitle">How we use and protect your personal data</p>
        </div>

        {/* Main Content */}
        <div className="privacy-content">
          {/* Pacific Cross Data Privacy Statement */}
          <div className="privacy-section">
            <h2 className="section-title">Pacific Cross Data Privacy Statement</h2>
            <p className="section-text">
              We respect and value your need for utmost care when handling the information that you share with us.
            </p>
            <p className="section-text">
              At Pacific Cross, we make sure we only ask for pieces of information that are absolutely necessary for us to provide 
              you with the right health or travel insurance and the most appropriate health assistance and travel services 
              whenever and wherever you may be.
            </p>
            <p className="section-text">
              It may therefore be necessary to share some of that information to healthcare providers and/or our contracted 
              administrators here and abroad. We ask for your trust - that we will handle your private data no less than if they were 
              our very own.
            </p>
            <p className="section-text">
              Throughout this Statement, "Pacific Cross" refers to either Pacific Cross Insurance, Inc. or Pacific Cross Health Care, 
              Inc. including its affiliated companies, branches, and subsidiaries.
            </p>
          </div>

          {/* Who is the Personal Information Controller */}
          <div className="privacy-section">
            <h2 className="section-title">Who is the Personal Information Controller?</h2>
            <p className="section-text">
              Pacific Cross is the Personal Information Controller under the Data Privacy Act of 2012 (hereafter referred to as 
              "DPA"), which means that it determines what purposes personal data held will be used for. It may also be that your 
              personal data is disclosed to third parties under a data sharing agreement, in which case, such third parties are also 
              the Personal Information Controllers.
            </p>
          </div>

          {/* What type of Information does Pacific Cross collect */}
          <div className="privacy-section">
            <h2 className="section-title">What type of Information does Pacific Cross collect?</h2>
            <p className="section-text">
              We collect personal information, including sensitive personal information, in order to service and administer the 
              insurance policy you bought and provide you with appropriate and timely health care and/or travel services. In 
              certain instances, personal data is collected in order to comply with legal requirements.
            </p>

            {/* A. For Medical Insurance Clients */}
            <div className="privacy-subsection">
              <h3 className="section-title">A. For Medical Insurance Clients</h3>
              <p className="section-text">The personal information we collect include:</p>
              <ul className="privacy-list">
                <li>Your name, photo and signature</li>
                <li>Your personal details, including gender, height, weight, and relationship to the beneficiary</li>
                <li>The name and signature of your dependents, beneficiary, spouse, authorized representative, parents, physician, 
                principal applicant-payor (if the insured is a minor), insurance agent</li>
                <li>Your dependents' personal details, including their height and weight</li>
                <li>Your contact details, including your relevant addresses, zip code, e-mail address and contact numbers</li>
                <li>Your insurance policy details, insurance claims details and supporting documents</li>
                <li>Your travel details, including itinerary, proof of overseas stay, and proof of enrollment, if on overseas study but 
                only when you have a claim for medical services abroad</li>
                <li>Your government records, including adoption papers, birth certificate, death certificate and tax-related 
                documents</li>
                <li>Your employment details, including your position/occupation, nature of work/business, name of employer and 
                employee advances</li>
                <li>Your financial details, including your credit card details such as credit card number, payment terms, cardholder's 
                name and cardholder's address, and bank details such as account name, account number, depositor's name, 
                depositor's signature for purposes of crediting claim payments</li>
              </ul>
              <p className="section-text">We also collect the following sensitive information:</p>
              <ul className="privacy-list">
                <li>Your personal details, including age, date of birth, citizenship, place of birth, marital status, thumbprint</li>
                <li>Your dependents' and beneficiaries' birthdates</li>
                <li>Your medical history, disability information, hospital records, diagnostic records, health record for diagnosed 
                illness or disability, previous ailments from other health insurers, prescriptions and other relevant medical/ 
                diagnostic/hospital reports</li>
                <li>Government issued documents, including Marriage Certificate, Tax Identification Number, SSS Number, 
                PhilHealth Number</li>
              </ul>
            </div>

            {/* B. For Travel Insurance Clients */}
            <div className="privacy-subsection">
              <h3 className="section-title">B. For Travel Insurance Clients</h3>
              <p className="section-text">The personal information we collect include:</p>
              <ul className="privacy-list">
                <li>Your name, photo and signature</li>
                <li>Your personal details, including gender, relationship to the beneficiary, relationship to dependent</li>
                <li>The name and signature of your beneficiary, dependent, spouse, authorized representative, account executive/ 
                broker/agent, travel agent, physician, principal applicant-payor (if the insured is a minor)</li>
                <li>Your contact details, including your relevant addresses, zip code, e-mail address and contact numbers</li>
                <li>Your insurance policy details, insurance claims details and supporting documents</li>
                <li>Your travel details, including your itinerary, date of departure and arrival and other travel documents</li>
                <li>Your government-issued records, including death certificate, marriage certificate, police report-related 
                documents</li>
                <li>Your employment details, including your position/occupation, nature of work/business and name of employer</li>
                <li>Your financial details, including your credit card details such as credit card number, payment terms, cardholder's 
                name, and cardholder's address, and bank details such as account name, account number, depositor's name, 
                depositor's signature</li>
              </ul>
              <p className="section-text">We also collect the following sensitive information:</p>
              <ul className="privacy-list">
                <li>Your personal details, including age, citizenship, date of birth, place of birth, civil status</li>
                <li>Your health details, including medical history, medical diagnoses, medical prescription, hospital details, and other 
                relevant medical/diagnostic/hospital reports</li>
                <li>Government ID Numbers, including Tax Identification Number, SSS ID, Driver's License Number, PhilHealth Number, 
                Pag-Ibig Number, Passport Number</li>
                <li>Government records, including birth certificate</li>
              </ul>
              <p className="section-text">
                When you provide your mobile phone number as your contact phone number, you consent to the use of your mobile 
                phone number for the purposes identified in this Statement. If you choose to receive notifications from us on your 
                mobile device (e.g., text notifications), you also consent to the use of your mobile phone number for that purpose.
              </p>
            </div>
          </div>

          {/* How does Pacific Cross collect your information */}
          <div className="privacy-section">
            <h2 className="section-title">How does Pacific Cross collect your information?</h2>
            <p className="section-text">
              You may choose to give your personal data in a variety of ways. For example, you may give your name and contact 
              information to communicate with us, to inquire about or to procure an insurance product, to make a complaint or give 
              feedback. Whenever you request for services, we will ask you to provide relevant, detailed and accurate information 
              pertinent to your request so we may ask you to update information that we have previously collected 
              so that we can render your requested services. You may inform us of the specific personal data you do not want to be 
              processed beyond the requested purpose. We will respect your request insofar as it is feasible to fulfill the purpose 
              for which the personal data was collected.
            </p>
            <p className="section-text">
              The personal data we collect about you may also come from our contracted medical professionals and/or your 
              attending physicians, intermediaries such as your broker or agent, your employer, your beneficiaries, affiliates or 
              other third parties (such as Emergency Assistance providers, other insurance companies, etc.).
            </p>
            <p className="section-text">
              If you disclose personal data about other individuals (such as your employees, dependents, beneficiaries), you 
              warrant that you have obtained their consent prior to your sharing of their personal data with us.
            </p>
            <p className="section-text">Pacific Cross may also obtain information online when you:</p>
            <ol className="privacy-numbered-list">
              <li>
                <strong>Visit the Pacific Cross Website or any of our online sites including our mobile application to purchase a policy, 
                submit an application, or claim for inquire about our services or network.</strong> By using the Pacific 
                Cross Website or any of Pacific Cross's online sites and mobile applications, you agree to the processing of your 
                personal and sensitive personal information as explained in this Statement. You also agree to our use of cookies 
                and the Google Analytics service integrated in [https://www.google.com/intl/en/policies/privacy/] so that we may 
                analyze statistics on the usage of our websites so that we may improve our customers' online and digital 
                experience. Please be informed that the Pacific Cross websites and mobile applications may include links to other 
                websites not owned and operated by Pacific Cross. These websites also collect information about you in 
                accordance with their privacy statements. We encourage you to read the privacy statements of these websites 
                as Pacific Cross will not be responsible for the content or privacy practices of websites not directly administered 
                by us.
              </li>
              <li>
                <strong>Engage with Pacific Cross on Social Media.</strong> When you integrate your access to any of our online sites with your 
                social media account, we allow for syncing access to your social media accounts (e.g., photos, contacts, 
                birthdays, likes or preferences). Except for information shared with us through private messaging, any personal 
                data you share when you interact with us on social media will be made public. Please make sure to control 
                whatever information you share with us through social media through privacy settings.
              </li>
              <li>
                <strong>Engage with Pacific Cross on Messaging Applications such as Viber, WhatsApp, or FB Messenger.</strong> Your 
                engagement with us on these messaging applications will be subject to the privacy statements of these third-
                party applications. Please read through them as private information shared through these apps may be used by 
                the operators of these applications for purposes other than what Pacific Cross.
              </li>
              <li>
                <strong>Access of our Website or any of our online sites through your mobile devices.</strong> If you access our website or any 
                of our online sites on your mobile phone or mobile device, we may also collect your unique device identifier and 
                mobile device IP address, as well as information about your device's operating system, mobile carrier, and your 
                location information.
              </li>
            </ol>
          </div>

          {/* How does Pacific Cross use your personal data */}
          <div className="privacy-section">
            <h2 className="section-title">How does Pacific Cross use your personal data?</h2>
            <p className="section-text">
              By providing Pacific Cross with personal data, you authorize and consent to our collection, use and processing of 
              your personal data for the following purposes:
            </p>
            <ol className="privacy-numbered-list">
              <li>
                <strong>Process and evaluate applications for insurance products and services</strong>
                <ul className="privacy-sublist">
                  <li>Carry out and/or verify any eligibility, credit, physical, medical, security, underwriting and/or identity checks in 
                  connection with our services and products</li>
                  <li>Determine the amount of premium and inform you of the same</li>
                  <li>Determine your insurance coverage and inform you of the same, including the exclusions from the coverage of 
                  the policy (any)</li>
                  <li>Deliver your Insurance Policy (for Medical clients) or Official Confirmation of Coverage (for Travel clients)</li>
                  <li>Make payments of commissions to our insurance agents</li>
                </ul>
              </li>
              <li>
                <strong>Process billing and collection of insurance premiums and related fees</strong>
                <ul className="privacy-sublist">
                  <li>Issuance of billing/invoice, and communicate with you by phone, e-mail or other means to inform you of the 
                  amount that you need to pay</li>
                  <li>Process your payment through credit card, debit notes and other means</li>
                  <li>Issuance of receipts for payments made</li>
                  <li>Generate, issue and deliver statements of account</li>
                  <li>Reconcile our accounting records with actual collections and ensure completeness of amounts deposited</li>
                </ul>
              </li>
              <li>
                <strong>Process insurance claims, and any purpose in connection with any claims made in respect of any of our services 
                and products, including making, defending, analyzing, investigating, processing, assessing, determining, 
                responding to, resolving or settling such claims</strong>
                <ul className="privacy-sublist">
                  <li>Obtain documents and validate information relevant to your claim from third parties such as (but not limited 
                  to) hospitals, other insurance providers and medical professionals</li>
                  <li>Process payments for approved claims, including (but not limited to) reimbursements and issuance of letters 
                  of authority</li>
                  <li>Review and resolution of denied insurance claims</li>
                </ul>
              </li>
              <li>
                <strong>Access of our Website or any of our online sites through your mobile devices.</strong> If you access our website or any 
                of our online sites on your mobile phone or mobile device, we may also collect your unique device identifier and 
                mobile device IP address, as well as information about your device's operating system, mobile carrier, and your 
                location information.
              </li>
              <li>
                <strong>Communicate with you regarding your insurance policy</strong>
                <ul className="privacy-sublist">
                  <li>Update your account information</li>
                  <li>Receive and attend to your questions, concerns, claims and feedback through calls, e-mail and other means</li>
                  <li>Generate reports regarding your questions, concerns, claims and feedback</li>
                  <li>Address your questions, concerns, claims and feedback</li>
                  <li>Renew and/or amend your policy</li>
                  <li>Cancel your insurance policy and refund amount paid</li>
                </ul>
              </li>
              <li>
                <strong>Conduct statistical and actuarial research, including conducting surveys, data analytics, market studies that aim 
                to continuously improve our marketing campaigns, products and services</strong>
              </li>
              <li>
                <strong>Carry out lawful business activities, including, but not limited to:</strong>
                <ul className="privacy-sublist">
                  <li>Maintaining credit and risk models, maintaining and updating Pacific Cross's information technology systems, 
                  product and business development and client servicing</li>
                  <li>Develop intelligent and automated systems</li>
                  <li>Conducting company audits or investigate a complaint or security threat</li>
                  <li>Comply with policy administration requirements</li>
                </ul>
              </li>
              <li>
                <strong>Comply with statutory and regulatory requirements, including directives, issuances by, or obligations of Pacific 
                Cross to any competent authority, regulator, supervisory body, enforcement agency, exchange, court, quasi-
                judicial body or tribunal</strong>
              </li>
              <li>
                <strong>Enable Pacific Cross to exercise sound corporate governance over its businesses, ensure that risks arising 
                therefrom are duly identified, measured, managed and mitigated, and enhance risk assessment and prevent 
                fraud</strong>
              </li>
              <li>
                <strong>Establish, exercise or defend legal claims</strong>
              </li>
              <li>
                <strong>Fulfill any other purposes directly related to the above-stated purposes</strong>
              </li>
            </ol>
            <p className="section-text">
              For purposes outside of those identified above, we will ask for your specific consent.
            </p>
          </div>

          {/* Will Pacific Cross share your personal data */}
          <div className="privacy-section">
            <h2 className="section-title">Will Pacific Cross share your personal data?</h2>
            <p className="section-text">
              Your personal data may be disclosed to third parties to enable Pacific Cross to achieve the purposes set out above, 
              including but not limited to the following:
            </p>
            <ul className="privacy-list">
              <li>Carry out lawful business activities</li>
              <li>Comply with statutory requirements like reporting to the Insurance Commission</li>
              <li>Respond to law enforcement authorities or other government regulatory bodies' requests</li>
              <li>Prevent physical harm or financial loss</li>
              <li>As a requirement of business transfer involving mergers and/or product and service collaboration</li>
              <li>Process insurance and reinsurance claims</li>
              <li>Carry out digitization and storage processes</li>
              <li>Carry out data collection and analysis</li>
              <li>Conduct company audits or investigate a complaint or security threat</li>
              <li>Establish, exercise or defend legal claims</li>
            </ul>
            <p className="section-text">
              When the processing of personal data is outsourced to a third party, the processing will be subject to written 
              agreements between us and the third parties processing the data, in accordance with the requirements of the Data 
              Privacy Act of 2012. These written agreements specify the rights and obligations of each party and will provide that 
              the third party has adequate security measures in place and will only process your personal data in our specific 
              written instructions.
            </p>
            <p className="section-text">
              We may also transfer your personal data to third parties as required by law or legal instrument, to protect our rights 
              or assets, to facilitate the acquisition or disposition of our businesses and in emergencies where the health or safety 
              of a person is endangered.
            </p>
            <p className="section-text">
              We will not sell, rent, share, trade, or disclose any of your personal data to any other party without your prior written 
              consent, with the exception of entities within our company and any third-party service provider which we have 
              engaged whose services necessarily require the processing of your personal data.
            </p>
            <p className="section-text">The following are the third parties to whom the information may be disclosed:</p>
            <ol className="privacy-numbered-list">
              <li>
                <strong>Service providers and partners.</strong> These are the medical specialists and other professionals who attend to your 
                medical needs. These will include Emergency Assistance partners who provide assistance to our insured outside 
                of Metro Manila or abroad.
              </li>
              <li>
                <strong>Business partners.</strong> Your broker or agent who services you may be given private or sensitive information through 
                communications to you that are outsourced through them or where good business practice requires them to be 
                provided with copies. Our business partners also include third party developers that create and operate mobile 
                apps and online platforms. These mobile apps and online platforms aim to provide the following (without 
                limitation): (i) convenient way for you to access our products and services, (ii) options and value-added 
                features to your plan, and/or (iii) notifications on other products and services that Pacific Cross offers to its 
                clients. Your personal data may be disclosed to these business partners when you download and use their apps 
                or visit their online platforms. Please take time to read the terms of use and privacy policy relevant to these apps 
                and online platforms.
              </li>
              <li>
                <strong>Regulatory Bodies/Agencies and other Legal Bodies.</strong> The Insurance Commission who regulates the industry 
                requires statistical reports which may include specific risk data information on a regular basis to help review and/or 
                modify regulations. The Bureau of Internal Revenue (BIR), Anti-Money Laundering Council, the Department of 
                Health also require reports where private data maybe shared from time to time.
              </li>
            </ol>
          </div>

          {/* How does Pacific Cross store your data */}
          <div className="privacy-section">
            <h2 className="section-title">How does Pacific Cross store your data?</h2>
            <p className="section-text">
              For the most part, electronic data are stored on premise in secure servers, secure cloud storage, and on the local 
              drives of Pacific Cross employees and officers, while physical documents submitted to us are kept on premises in 
              secure cabinets for a year after which, they are transferred to off-site document storage facilities with stringent 
              data security measures in place.
            </p>
            <p className="section-text">
              Some of the collection and processing, and therefore initial storage, are done using cloud technology. We utilize 
              three (3) cloud hosting services located in Manila, Singapore, and Australia. All are certified not only by US and EU 
              data protection agencies but also by US and EU data protection agencies. By providing us with your information or using our 
              online sites and mobile applications or by purchasing a policy with international coverage, you consent to the 
              collection, storage, and processing of your personal information abroad.
            </p>
            <p className="section-text">
              Generally, your personal data will be retained by Pacific Cross during the term of your policy or plan and/or until the 
              expiration of the retention limit standards set by Pacific Cross and the industry and laws and regulations reckoned 
              from account closure. After such periods, your information will either be destroyed or anonymized.
            </p>
          </div>

          {/* Are you allowed to access and update your information */}
          <div className="privacy-section">
            <h2 className="section-title">Are you allowed to access and update your information?</h2>
            <p className="section-text">
              Pacific Cross encourages all its insured to keep their personal data current and updated. You may review personal 
              data we have processed in your copy of the policy document. You may also check them out when you successfully 
              register in any of our mobile applications. You may likewise call our Customer Service Department and validate any 
              personal data we may have stored and request them to be updated. Finally, you may e-mail us to request your 
              personal details updated. When you request for access to your personal data, we will take reasonable steps to 
              confirm your identity before granting you access and updating your information.
            </p>
          </div>

          {/* How does Pacific Cross protect your personal data */}
          <div className="privacy-section">
            <h2 className="section-title">How does Pacific Cross protect your personal data?</h2>
            <p className="section-text">
              Pacific Cross utilizes the following known best practices to reasonably protect your data from unauthorized access, 
              use, and disclosure:
            </p>
            <ol className="privacy-numbered-list">
              <li>
                <strong>Employee Data Security Awareness and Training.</strong> This is a basic required training program for all new employees. 
                In addition, refresher courses are conducted especially to units who handle the personal data of clients.
              </li>
              <li>
                <strong>Data Masking and Encryption.</strong> Only those authorized to process personal data are actually allowed to view 
                them. We employ data masking and e-mail encryption. Employees who handle such data go through additional 
                training for data security handling.
              </li>
              <li>
                <strong>User Access Control.</strong> We manage user access strictly with regular reviews and prompts for change passwords. 
                Quarterly review by supervisors of authorized users ensures access updates are current.
              </li>
              <li>
                <strong>Regular System and Operational Audits.</strong> Annual third party audits are conducted to ensure compliance and 
                address any security weaknesses identified.
              </li>
              <li>
                <strong>Data Sharing Contracts.</strong> When private data is required to be shared to service providers and business partners, 
                this privilege is explicitly defined, the contract stating the conditions around data sharing, the limitations of 
                usage, and the diligence required for its storage.
              </li>
            </ol>
            <p className="section-text">What are your rights as a Data Subject?</p>
          </div>

          {/* What are your rights as a Data Subject */}
          <div className="privacy-section">
            <h2 className="section-title">What are your rights as a Data Subject?</h2>
            <p className="section-text">Under the Data Privacy Act of 2012, you have the right to:</p>
            <ol className="privacy-numbered-list">
              <li>
                <strong>Access Personal Data</strong><br />
                Under the DPA, it is possible for individuals to request access to any of their personal data held by the Pacific 
                Cross, subject to certain restrictions. A request for disclosure of such information is called a Subject Access 
                Request. Any such requests should be addressed to Pacific Cross Customer Service.
              </li>
              <li>
                <strong>Make corrections to Personal Data</strong><br />
                The DPA requires Pacific Cross to take reasonable steps to ensure that any personal data it processes is accurate 
                and up-to-date. It is your responsibility to inform us of any changes to your Personal Data that you have supplied 
                during the course of your engagement.
              </li>
              <li>
                <strong>Object to the processing of Personal Data</strong><br />
                You shall have the right to object to the processing of your Personal Data, including processing for direct 
                marketing, automated processing or profiling. You shall also be notified and be given an opportunity to withhold 
                consent to the processing in case of changes or any amendment to the information supplied or declared to the 
                data subject.
              </li>
              <li>
                <strong>Erasure or blocking of Personal Data</strong><br />
                You shall have the right to suspend, withdraw or order the blocking, removal or destruction of your Personal Data 
                from our filing system.
              </li>
              <li>
                <strong>Be informed of the existence of processing of Personal Data</strong><br />
                You have a right to be informed whether Personal Data pertaining to you shall be, are being or have been 
                processed, including the existence of automated decision-making and profiling.
              </li>
              <li>
                <strong>Damages</strong><br />
                Upon presentation of a valid decision, you have the right to be indemnified for any damages sustained due to 
                inaccurate, incomplete, outdated, false, unlawfully obtained or unauthorized use of Personal Data, taking into 
                account any violation of your rights and freedoms as a data subject.
              </li>
              <li>
                <strong>Lodge a complaint before the National Privacy Commission</strong>
              </li>
            </ol>
          </div>

          {/* Contact Us */}
          <div className="privacy-section">
            <h2 className="section-title">Contact Us</h2>
            <p className="section-text">
              For questions or concerns relating to this Statement, you may get in touch with us through the following:
            </p>
            <div className="privacy-statement-contact-info">
              <p className="contact-title"><strong>Pacific Cross Data Privacy Office</strong></p>
              <p className="contact-detail">E-mail: DPO@pacificcross.com.ph</p>
              <p className="contact-detail">Tel. No.: +63 2 8230 8588</p>
              <p className="contact-detail">Address: 18th Floor, 8 Rockwell Building,</p>
              <p className="contact-detail">Hidalgo Drive, Makati City, Metro Manila, Philippines</p>
              
              <p className="contact-title"><strong>Angelo V. Gamay</strong></p>
              <p className="contact-detail">Data Protection Officer</p>
              <p className="contact-detail">for Pacific Cross Insurance, Inc. and Pacific Cross Health Care, Inc.</p>
              <p className="contact-detail">E-mail: DPO.insurance@pacificcross.com.ph, DPO.healthcare@pacificcross.com.ph</p>
              <p className="contact-detail">Tel. No.: + 63 2 8899-8001 loc. 8504</p>
            </div>
          </div>

          {/* Changes to this Statement */}
          <div className="privacy-section">
            <h2 className="section-title">Changes to this Statement</h2>
            <p className="section-text">
              The Pacific Cross Privacy Statement may be updated from time to time. Please periodically review our Privacy 
              Statement (available at www.pacificcross.com.ph) to be apprised of our latest updates. This Statement was last 
              updated on March 16, 2018.
            </p>
          </div>

          {/* Digital Marketing Statement */}
          <div className="privacy-section">
            <h2 className="section-title">Digital Marketing Statement</h2>
            <p className="section-text">
              Pacific Cross, as permitted by law and with the consent of the client, prospective client or website visitor, may use 
              the name and contact details already provided for marketing and promotional communications such as e-mail, 
              phone calls and SMS containing news, offers, promotions and events offered by the company, our affiliates and 
              service providers in relation to our products and services. Clients, prospective clients or website visitors are given 
              the option to unsubscribe to stop receiving marketing messages should they wish to no longer receive marketing and 
              promotional communications from Pacific Cross. In addition, our clients, prospective clients or website visitors may 
              receive questionnaire, survey or feedback request through e-mail, phone calls and SMS for the purpose of helping 
              the company improve the overall client experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyStatementSection;